import { useState, useRef, useEffect, useCallback } from "react";
import AdminLayout from "@/components/layout-admin";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Send, Sparkles, Trash2, Lightbulb, TrendingUp, Users, BarChart3, Shield, CheckCircle2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { queryClient } from "@/lib/queryClient";
import { useAuth } from "@/hooks/use-auth";

interface ChatMessage {
  role: "user" | "assistant" | "action";
  content: string;
}

const QUESTION_CATEGORIES = [
  {
    icon: BarChart3,
    label: "Overview",
    questions: [
      "Give me a full overview of my business right now",
      "Break down my revenue by service type for the last 30 days",
    ],
  },
  {
    icon: Users,
    label: "Team",
    questions: [
      "Who is my top performing cleaner and why?",
      "Analyze my staffing — am I overstaffed or understaffed?",
    ],
  },
  {
    icon: TrendingUp,
    label: "Growth",
    questions: [
      "Create an attack plan to hit $10k/month in cleaning revenue",
      "What's my current occupancy rate and how can I improve it?",
    ],
  },
  {
    icon: Shield,
    label: "Risk",
    questions: [
      "What are the biggest risks to my business right now?",
      "How many cleaners do I need to handle 50 jobs per month?",
    ],
  },
];

const MAX_STORED_MESSAGES = 50;

export default function AdminLumi() {
  const { user } = useAuth();
  const historyKey = user ? `lumi_history_${user.organizationId}_${user.id}` : null;

  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    if (!historyKey) return [];
    try {
      const stored = localStorage.getItem(historyKey);
      return stored ? (JSON.parse(stored) as ChatMessage[]) : [];
    } catch {
      return [];
    }
  });
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!historyKey) return;
    try {
      localStorage.setItem(historyKey, JSON.stringify(messages.slice(-MAX_STORED_MESSAGES)));
    } catch {}
  }, [messages, historyKey]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const sendMessage = useCallback(async (messageText?: string) => {
    const text = (messageText || input).trim();
    if (!text || isStreaming) return;

    const userMessage: ChatMessage = { role: "user", content: text };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setIsStreaming(true);

    try {
      const response = await fetch("/api/admin/lumi/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          message: text,
          history: newMessages.slice(-10),
        }),
      });

      if (!response.ok) throw new Error("Failed to send message");

      const reader = response.body?.getReader();
      if (!reader) throw new Error("No response stream");

      const decoder = new TextDecoder();
      let assistantContent = "";
      setMessages(prev => [...prev, { role: "assistant", content: "" }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.action) {
                setMessages(prev => {
                  const lastIsEmpty = prev.length > 0 && prev[prev.length - 1].role === "assistant" && !prev[prev.length - 1].content;
                  const base = lastIsEmpty ? prev.slice(0, -1) : prev;
                  return [...base, { role: "action" as const, content: data.action }, { role: "assistant", content: assistantContent }];
                });
                queryClient.invalidateQueries({ queryKey: ["/api/admin/cleaning-jobs"] });
                queryClient.invalidateQueries({ queryKey: ["/api/maintenance"] });
                queryClient.invalidateQueries({ queryKey: ["/api/tickets"] });
                queryClient.invalidateQueries({ queryKey: ["/api/admin/finances"] });
                queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices"] });
                queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
              }
              if (data.content) {
                assistantContent += data.content;
                setMessages(prev => {
                  const updated = [...prev];
                  updated[updated.length - 1] = { role: "assistant", content: assistantContent };
                  return updated;
                });
              }
              if (data.done) break;
              if (data.error) {
                assistantContent += "\n\n*Error: " + data.error + "*";
                setMessages(prev => {
                  const updated = [...prev];
                  updated[updated.length - 1] = { role: "assistant", content: assistantContent };
                  return updated;
                });
              }
            } catch {}
          }
        }
      }
    } catch (err: any) {
      setMessages(prev => [
        ...prev.filter(m => m.role !== "assistant" || m.content !== ""),
        { role: "assistant", content: "Sorry, I had trouble processing that. Please try again." },
      ]);
    } finally {
      setIsStreaming(false);
    }
  }, [input, isStreaming, messages]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setInput("");
    if (historyKey) {
      try { localStorage.removeItem(historyKey); } catch {}
    }
  };

  return (
    <AdminLayout>
      <div className="flex flex-col h-[calc(100vh-3.5rem)] sm:h-[calc(100vh-4rem)]" data-testid="lumi-page">
        {messages.length > 0 && (
          <div className="flex justify-end px-3 sm:px-4 md:px-6 py-1.5 border-b">
            <Button
              variant="ghost"
              size="sm"
              onClick={clearChat}
              className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
              data-testid="btn-clear-chat"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" />
              Clear
            </Button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-3 sm:px-4 md:px-6 py-3 sm:py-4 space-y-3 sm:space-y-4" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full max-w-xl mx-auto" data-testid="lumi-welcome">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-md mb-3 sm:mb-4">
                <Lightbulb className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h2 className="text-base sm:text-lg font-bold mb-1">What can I help with?</h2>
              <p className="text-xs text-muted-foreground text-center mb-4 sm:mb-6">
                I see your live data — apartments, bookings, finances, staff, and operations.
              </p>

              <div className="w-full space-y-3 sm:space-y-4">
                {QUESTION_CATEGORIES.map((cat, ci) => (
                  <div key={ci}>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <cat.icon className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{cat.label}</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {cat.questions.map((q, qi) => (
                        <button
                          key={qi}
                          onClick={() => sendMessage(q)}
                          className="text-left px-3 py-2 rounded-lg border bg-card hover:bg-accent hover:border-primary/20 transition-all text-xs sm:text-sm text-muted-foreground hover:text-foreground"
                          data-testid={`suggested-q-${ci * 2 + qi}`}
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              data-testid={`chat-msg-${i}`}
            >
              {msg.role === "action" ? (
                <div className="flex items-start gap-2 max-w-[90%] sm:max-w-[85%] md:max-w-[75%] rounded-xl px-3 sm:px-4 py-2 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 mr-8 sm:mr-12" data-testid={`action-msg-${i}`}>
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
                  <p className="text-sm text-emerald-800 dark:text-emerald-300">{msg.content}</p>
                </div>
              ) : (
                <div
                  className={`max-w-[90%] sm:max-w-[85%] md:max-w-[75%] rounded-2xl px-3 sm:px-4 py-2.5 sm:py-3 ${
                    msg.role === "user"
                      ? "bg-primary text-primary-foreground ml-8 sm:ml-12"
                      : "bg-muted mr-8 sm:mr-12"
                  }`}
                >
                  {msg.role === "assistant" ? (
                    <div className="prose prose-sm dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_h1]:text-base [&_h2]:text-sm [&_h3]:text-sm [&_p]:text-sm [&_li]:text-sm [&_table]:text-xs [&_th]:px-2 [&_td]:px-2 [&_th]:py-1 [&_td]:py-1">
                      <ReactMarkdown>{msg.content || "..."}</ReactMarkdown>
                    </div>
                  ) : (
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  )}
                </div>
              )}
            </div>
          ))}

          {isStreaming && messages[messages.length - 1]?.role !== "assistant" && (
            <div className="flex justify-start" data-testid="typing-indicator">
              <div className="bg-muted rounded-2xl px-4 py-3 mr-12">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Thinking...
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className="border-t bg-background px-3 sm:px-4 md:px-6 py-2 sm:py-2.5">
          <div className="flex items-end gap-2 max-w-3xl mx-auto">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about your business..."
              className="min-h-[38px] sm:min-h-[40px] max-h-[100px] resize-none rounded-xl border-muted-foreground/20 text-sm"
              rows={1}
              disabled={isStreaming}
              data-testid="input-lumi-message"
            />
            <Button
              onClick={() => sendMessage()}
              disabled={!input.trim() || isStreaming}
              size="icon"
              className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl shrink-0 bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
              data-testid="btn-send-message"
            >
              {isStreaming ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground text-center mt-1">
            Responses are AI-generated from your live data and should be verified.
          </p>
        </div>
      </div>
    </AdminLayout>
  );
}
